// src/components/CampionatoSelector.tsx
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDownIcon } from 'lucide-react'
import apiClient from '@/services/apiClient'
import { queryClient } from '@/lib/queryClient' // se non l'hai, sostituisci con il tuo queryClient

type Campionato = { id_campionato: number; campionato_name: string }

const LS_KEY = 'arma4you.campionato_id'

function readLS(): number | null {
  const raw = localStorage.getItem(LS_KEY)
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function writeLS(id: number) {
  localStorage.setItem(LS_KEY, String(id))
  // notifica globale
  window.dispatchEvent(new CustomEvent('campionato:changed', { detail: { id } }))
}

export default function CampionatoSelector({ className = '' }: { className?: string }) {
  const detailsRef = React.useRef<HTMLDetailsElement>(null)

  // 1) carica i campionati (gestisce array o payload paginato)
  const { data: items = [] } = useQuery({
    queryKey: ['campionati'],
    queryFn: async (): Promise<Campionato[]> => {
      const res = await apiClient.get<any>('campionati/', {
params: { ordering: 'id_campionato', page_size: 1000 },

      })
      const d = res.data
      return Array.isArray(d) ? d : (d?.results ?? [])
    },
    staleTime: 5 * 60 * 1000,
  })

  // 2) campionato selezionato (solo id in LS)
  const [selectedId, setSelectedId] = React.useState<number | null>(() => readLS())

  // 3) inizializzazione: se non c'è in LS, scegli id=1 (se esiste), altrimenti il primo
  React.useEffect(() => {
    if (!items || items.length === 0) return
    const fromLS = readLS()
    if (fromLS != null) {
      setSelectedId(fromLS)
      return
    }
    const id1 = items.find(c => c.id_campionato === 1)?.id_campionato
    const pick = id1 ?? items[0].id_campionato
    setSelectedId(pick)
    writeLS(pick)
  }, [items])

  // 4) label corrente
  const label = React.useMemo(() => {
    const f = items.find(c => c.id_campionato === selectedId)
    return f?.campionato_name ?? 'Seleziona campionato'
  }, [items, selectedId])

  // 5) handler selezione
  const onPick = (c: Campionato) => {
    setSelectedId(c.id_campionato)
    writeLS(c.id_campionato)
    // refetch immediato di tutte le query (così si aggiornano le liste)
    queryClient.invalidateQueries({ predicate: () => true })
    // chiudi dropdown
    detailsRef.current?.removeAttribute('open')
  }

  return (
    <div className={`header-selector relative ${className}`}>
      <details ref={detailsRef} className="group">
        <summary className="selector-trigger flex items-center cursor-pointer select-none">
          <span className="text-sm text-gray-700 mr-2">{label}</span>
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </summary>

        <div className="selector-menu absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-md z-50">
          <ul className="py-1 max-h-80 overflow-y-auto">
            {items.map((c) => (
              <li key={c.id_campionato}>
                <button
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    selectedId === c.id_campionato ? 'text-primary-600 font-medium' : 'text-gray-700'
                  }`}
                  onClick={() => onPick(c)}
                >
                  {c.campionato_name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  )
}
